import { useRef, useState } from "react";
import { PencilIcon, Trash2Icon } from "lucide-react";
import type { CmmdProjectUpdateStatus, CmmdProjectUpdateView } from "@cmmd-forge/contracts";

import { PersonAvatar } from "~/components/identity/PersonAvatar";
import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { cn } from "~/lib/utils";
import { PROJECT_UPDATE_STATUS_META } from "./ProjectUpdateStatusPicker";
import { ProjectUpdateComposerFields } from "./ProjectUpdateComposerFields";

export interface CmmdProjectUpdateCardProps {
  readonly projectId: string;
  readonly update: CmmdProjectUpdateView;
  /** Overview density clamps body for scanability. */
  readonly density?: "default" | "overview";
  readonly onUpdate: (
    updateId: string,
    input: { readonly status: CmmdProjectUpdateStatus; readonly body: string },
  ) => Promise<void>;
  readonly onDelete: (updateId: string) => Promise<void>;
}

export function CmmdProjectUpdateCard(props: CmmdProjectUpdateCardProps) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(props.update.status);
  const [body, setBody] = useState(props.update.body);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const meta = PROJECT_UPDATE_STATUS_META[props.update.status];
  const overviewDensity = props.density === "overview";
  const clampBody = overviewDensity && !expanded && props.update.body.trim().length > 120;

  const finishEditing = () => {
    setEditing(false);
    requestAnimationFrame(() => editButtonRef.current?.focus());
  };

  const cancel = () => {
    setStatus(props.update.status);
    setBody(props.update.body);
    setError(null);
    finishEditing();
  };

  const save = async () => {
    if (!body.trim() || body.length > 10_000) return;
    setSaving(true);
    setError(null);
    try {
      await props.onUpdate(props.update.id, { status, body });
      finishEditing();
    } catch {
      setError("Couldn’t save this update. Your draft is still here—try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await props.onDelete(props.update.id);
    } catch {
      setError("Couldn’t delete this update. Try again.");
    } finally {
      setDeleting(false);
    }
  };

  if (editing) {
    return (
      <ProjectUpdateComposerFields
        projectId={props.projectId}
        status={status}
        body={body}
        saving={saving}
        error={error}
        ariaLabel="Edit project status update"
        submitLabel="Save update"
        submittingLabel="Saving…"
        onStatusChange={setStatus}
        onBodyChange={setBody}
        onSubmit={() => void save()}
        onCancel={cancel}
      />
    );
  }

  return (
    <article
      className={cn(
        "group rounded-xl bg-muted/25 transition-colors duration-150 hover:bg-muted/35",
        overviewDensity ? "px-3.5 py-3" : "px-4 py-3.5",
      )}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", meta.text)}>
          <span className={cn("size-1.5 rounded-full", meta.dot)} aria-hidden />
          {meta.label}
        </span>
        {isEdited(props.update) ? (
          <span className="text-[11px] text-muted-foreground/60">Edited</span>
        ) : null}
        {props.update.createdAt ? (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground/70">
            {formatOverviewDate(props.update.createdAt)}
          </span>
        ) : null}
        {props.update.canEdit ? (
          <div
            className={
              props.update.createdAt ? "ml-1 flex items-center" : "ml-auto flex items-center"
            }
          >
            <button
              ref={editButtonRef}
              type="button"
              onClick={() => {
                setStatus(props.update.status);
                setBody(props.update.body);
                setError(null);
                setEditing(true);
              }}
              aria-label="Edit update"
              title="Edit update"
              className="-my-1 rounded p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
            >
              <PencilIcon className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void remove()}
              disabled={deleting}
              aria-label="Delete update"
              title="Delete update"
              className="-my-1 -mr-1 rounded p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40"
            >
              <Trash2Icon className="size-3.5" />
            </button>
          </div>
        ) : null}
      </div>
      <div className={clampBody ? "line-clamp-2" : undefined}>
        <MarkdownContent variant="compact">{props.update.body}</MarkdownContent>
      </div>
      {overviewDensity && props.update.body.trim().length > 120 ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
      {props.update.authorName ? (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <PersonAvatar
            name={props.update.authorName}
            avatarUrl={props.update.authorAvatarUrl}
            className="flex size-5 shrink-0 items-center justify-center rounded-full bg-background/80 text-[8px] font-medium text-muted-foreground"
          />
          {props.update.authorName}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </article>
  );
}

function isEdited(update: CmmdProjectUpdateView): boolean {
  const createdAt = Date.parse(update.createdAt);
  const updatedAt = Date.parse(update.updatedAt);
  return Number.isFinite(createdAt) && Number.isFinite(updatedAt) && updatedAt > createdAt;
}

export function formatOverviewDate(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
